import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useTracking } from "@/hooks/useInteractionTracking";
import { supabase } from "@/integrations/supabase/client";
import AvatarUpload from "./AvatarUpload";
import ProfileFormFields from "./ProfileFormFields";
import ProfileFormActions from "./ProfileFormActions";
import { displayName } from "@/lib/utils";
import { parseAsString, useQueryState } from "nuqs";
import PasswordForm from "./PasswordForm";
import { ManualPartnerCodeRedemption } from "./ManualPartnerCodeRedemption";

interface ProfileFormProps {
  profile: {
    first_name?: string;
    last_name?: string;
    bio?: string;
    phone?: string;
    avatar_url?: string;
    referral_code: string;
  };
  onProfileUpdate: () => void;
}

const ProfileForm = ({ profile, onProfileUpdate }: ProfileFormProps) => {
  const { toast } = useToast();
  const { trackFeatureUsage, trackClick } = useTracking();
  const [pageAction, setPageAction] = useQueryState<string>(
    "action",
    parseAsString.withDefault("").withOptions({ clearOnDefault: true })
  );

  const isEditingProfile = pageAction === "edit-profile";
  const isUpdatingPassword = pageAction === "update-password";


  const [formData, setFormData] = useState({
    first_name: profile.first_name || "",
    last_name: profile.last_name || "",
    bio: profile.bio || "",
    phone: profile.phone || "",
    avatar_url: profile.avatar_url || "",
  });

  const updateProfile = useMutation({
    mutationFn: async (data: typeof formData) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { error } = await supabase
        .from("profiles")
        .update({
          first_name: data.first_name,
          last_name: data.last_name,
          bio: data.bio,
          phone: data.phone,
          avatar_url: data.avatar_url,
        })
        .eq("id", user.id);

      if (error) throw error;
    },
    onSuccess: () => {
      trackFeatureUsage("profile", "update_success");
      toast({
        title: "Success",
        description: "Profile updated successfully",
      });
      setPageAction("");
      onProfileUpdate();
    },
    onError: (error) => {
      console.error('Error updating profile:', error);
      toast({
        title: "Error",
        description: "Failed to update profile",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    trackFeatureUsage("profile", "update_attempt");
    updateProfile.mutate(formData);
  };

  const handleAvatarUpload = (url: string) => {
    trackClick("avatar_upload");
    setFormData(prev => ({ ...prev, avatar_url: url }));
    updateProfile.mutate({
      ...formData,
      avatar_url: url,
    });
  };

  const handleUpdatePassword = async (newPassword: string) => {
    trackFeatureUsage("profile", "password_update_attempt");
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;
      
      trackFeatureUsage("profile", "password_update_success");
      toast({
        title: "Success",
        description: "Password updated successfully",
      });

      setPageAction("");
    } catch (error) {
      console.error('Error updating password:', error);
      trackFeatureUsage("profile", "password_update_failed");
      toast({
        title: "Error",
        description: "Failed to update password",
        variant: "destructive",
      });
    }
  };

  const handleFieldChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCancel = () => {
    setPageAction("");
    setFormData({
      first_name: profile.first_name || "",
      last_name: profile.last_name || "",
      bio: profile.bio || "",
      phone: profile.phone || "",
      avatar_url: profile.avatar_url || "",
    });
  };

  const fullName = displayName(formData.first_name, formData.last_name);

  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="flex flex-col items-center gap-4">
          <AvatarUpload url={formData.avatar_url} onUpload={handleAvatarUpload} />
          {!isEditingProfile && fullName && (
            <div className="text-center">
              <h2 className="text-xl font-semibold text-foreground">{fullName}</h2>
              {formData.bio && (
                <p className="text-muted-foreground mt-1">{formData.bio}</p>
              )}
              <div className="w-auto mx-auto text-sm text-muted-foreground mt-1 space-x-2">
                <span>Referral code:</span>
                <code className="text-foreground">{profile.referral_code}</code>
              </div>
            </div>
          )}
        </div>

        {(isEditingProfile || !fullName) && (
          <ProfileFormFields
            formData={formData}
            isEditing={isEditingProfile}
            onChange={handleFieldChange}
          />
        )}

        {isUpdatingPassword && (
          <PasswordForm
            onUpdatePassword={handleUpdatePassword}
            onCancel={() => setPageAction("")}
          />
        )}

        {!isUpdatingPassword && <ProfileFormActions
          isEditing={isEditingProfile}
          isPending={updateProfile.isPending}
          onEdit={() => setPageAction("edit-profile")}
          onUpdatePassword={() => setPageAction("update-password")}
          onCancel={handleCancel}
        />}
      </form>
      
      {/* Manual partner code redemption UI */}
      <ManualPartnerCodeRedemption />
    </div>
  );
};

export default ProfileForm;
